import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/ui/Button';
import { Field } from '../../../shared/ui/Field';
import { ApiError, NetworkError, isNetworkError } from '../../../shared/api/client';
import { useAuthStore } from '../model/auth.store';
import {
  hasFieldErrors,
  validateRegisterForm,
  type FieldErrors,
} from '../model/auth.validation';

export default function RegisterPage() {
  const status = useAuthStore((state) => state.status);
  const actionStatus = useAuthStore((state) => state.actionStatus);
  const registerAction = useAuthStore((state) => state.register);

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const navigate = useNavigate();

  if (status === 'authenticated') {
    return <Navigate to="/today" replace />;
  }

  const pending = actionStatus === 'pending';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const validation = validateRegisterForm({
      login,
      password,
      confirm_password: confirmPassword,
      invite_code: inviteCode,
    });
    if (hasFieldErrors(validation)) {
      setErrors(validation);
      return;
    }
    setErrors({});

    try {
      await registerAction(login.trim(), password, inviteCode.trim());
      navigate('/today', { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
        return;
      }
      if (isNetworkError(error) || error instanceof NetworkError) {
        setFormError('Нет соединения с сервером. Регистрация требует сети.');
        return;
      }
      setFormError('Не удалось зарегистрироваться. Попробуйте ещё раз.');
    }
  }

  return (
    <div className="tg-page flex min-h-dvh items-center justify-center">
      <div className="w-full max-w-md space-y-6">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-tg-accent">Новый аккаунт</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-tg-text">Регистрация</h1>
          <p className="mt-2 text-sm text-tg-text-secondary">
            Регистрация доступна только по коду приглашения от администратора.
          </p>
        </header>

        <form className="tg-card space-y-4 p-5 sm:p-6" onSubmit={handleSubmit} noValidate>
          <h2 className="text-lg font-semibold text-tg-text">Создать аккаунт</h2>

          {formError ? (
            <div
              className="rounded-xl border border-tg-danger/40 bg-tg-danger-soft px-3 py-2 text-sm text-tg-danger"
              role="alert"
            >
              {formError}
            </div>
          ) : null}

          <Field
            label="Код приглашения"
            name="invite_code"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={inviteCode}
            error={errors.invite_code ?? null}
            onChange={(event) => {
              setInviteCode(event.target.value);
            }}
          />

          <Field
            label="Логин"
            name="login"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={login}
            error={errors.login ?? null}
            onChange={(event) => {
              setLogin(event.target.value);
            }}
            hint="Латинские буквы, цифры, дефис и подчёркивание"
          />

          <Field
            label="Пароль"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            error={errors.password ?? null}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            hint="Минимум 8 символов"
          />

          <Field
            label="Повторите пароль"
            name="confirm_password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            error={errors.confirm_password ?? null}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
            }}
          />

          <Button type="submit" loading={pending} className="w-full">
            Зарегистрироваться
          </Button>

          <p className="text-center text-sm text-tg-text-secondary">
            Уже есть аккаунт?{' '}
            <Link className="text-tg-accent hover:text-tg-accent-hover" to="/login">
              Войти
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
